import { TranslationStatusEnum } from './generated/gqlTypes'
import { BASE_PATH, WHOLESALE_ROUTES } from './SPA/urls'
import { getCurrencySymbol } from './SPA/utils/misc'

export const USER_TRANSLATION_STATUS_MAPPING = (t, status: TranslationStatusEnum): string => {
  switch (status) {
    case TranslationStatusEnum.NotTranslated:
      return t('translation.statusNotTranslated', 'Not translated')
    case TranslationStatusEnum.PartiallyTranslated:
      return t('translation.statusPartiallyTranslated', 'Partially translated')
    case TranslationStatusEnum.FullyTranslated:
      return t('translation.statusFullyTranslated', 'Fully translated')
    case TranslationStatusEnum.Approved:
      return t('translation.statusApproved', 'Approved')
    default:
      return t('translation.statusNotTranslated', 'Not translated')
  }
}

// export const USER_TRANSLATION_ENTITY_TYPE_MAPPING = {
//   [UserTranslationEntityType.Product]: 'Product',
//   [UserTranslationEntityType.SiteSettings]: 'Settings',
//   [UserTranslationEntityType.WholesaleApp]: 'Wholesale app',
//   [UserTranslationEntityType.ProductSpecifics]: 'Reflection',
//   [UserTranslationEntityType.Category]: 'Category',
//   [UserTranslationEntityType.Attribute]: 'Attribute',
//   [UserTranslationEntityType.WholesaleProduct]: 'Wholesale product',
//   [UserTranslationEntityType.ChoiceValue]: 'Choice value',
//   [UserTranslationEntityType.Offer]: 'Offer',
//   [UserTranslationEntityType.Variation]: 'Variant',
//   [UserTranslationEntityType.Collection]: 'Collection',
//   [UserTranslationEntityType.VariationProperty]: 'Variation property',
//   [UserTranslationEntityType.Article]: 'Article',
//   [UserTranslationEntityType.BlogCategory]: 'Blog category',
//   [UserTranslationEntityType.Blog]: 'Blog',
//   [UserTranslationEntityType.Voucher]: 'Voucher',
//   [UserTranslationEntityType.Sale]: 'Sale',
//   [UserTranslationEntityType.PromoCode]: 'Promo code',
//   [UserTranslationEntityType.RewardProgram]: 'Reward program',
//   [UserTranslationEntityType.SiteBanner]: 'Site banner',
//   [UserTranslationEntityType.BannerCategory]: 'Banner category',
//   [UserTranslationEntityType.FooterBlock]: 'Footer block',
//   [UserTranslationEntityType.FooterLink]: 'Footer link',
//   [UserTranslationEntityType.WholesaleApp_7]: 'Wholesale product element',
//   [UserTranslationEntityType.Group]: 'Group',
//   [UserTranslationEntityType.NonModelMailing]: 'Non model mailing',
//   [UserTranslationEntityType.NonModelFrontend]: 'Non model frontend',
// }
type FormError =
  | 'valueIsRequired'
  | 'mustBeChecked'
  | 'maxLength'
  | 'tooLongWithValue'
  | 'tooShortWithValue'
  | 'invalidEmail'
  | 'invalidURL'
export const FORM_ERRORS_MAPPING = (t, errorType: FormError, values?: { max?: number; min?: number }): string => {
  switch (errorType) {
    case 'valueIsRequired':
      return t('formErrors.valueIsRequired', 'This value is required')
    case 'mustBeChecked':
      return t('formErrors.fieldMustBeChecked', 'Field must be checked')
    case 'maxLength':
      return t('formErrors.maxLength', { defaultValue: 'Must be at most {{max}} characters', max: values.max })
    case 'tooLongWithValue':
      return t('formErrors.tooLongWithValue', { defaultValue: 'Too long! (max {{max}})', max: values.max })
    case 'tooShortWithValue':
      return t('formErrors.tooShortWithValue', { defaultValue: 'Too short (min {{min}})', min: values.min })
    case 'invalidEmail':
      return t('formErrors.invalidEmail', 'Invalid email')
    case 'invalidURL':
      return t('formErrors.invalidURL', 'Please enter a valid URL')
    default:
      return 'Invalid'
  }
}

export const MAXIMUM_FRACTION_DIGITS_BY_CURRENCY = {
  USD: 2,
  EUR: 2,
  UAH: 0,
}

export const PRICE_LOCALE_BY_CURRENCY: Record<string, string> = {
  USD: 'en-US',
  EUR: 'en-IE',
  GBP: 'en-GB',

  CAD: 'en-CA',
  AUD: 'en-AU',
  NZD: 'en-NZ',

  JPY: 'ja-JP',
  CNY: 'zh-CN',
  HKD: 'zh-HK',
  SGD: 'en-SG',

  CHF: 'de-CH',
  SEK: 'sv-SE',
  NOK: 'nb-NO',
  DKK: 'da-DK',

  PLN: 'pl-PL',
  CZK: 'cs-CZ',
  HUF: 'hu-HU',
  RON: 'ro-RO',

  UAH: 'uk-UA',

  MXN: 'es-MX',
  BRL: 'pt-BR',
}

export const CURRENCY_OPTIONS = [
  {
    value: 'USD',
    label: `US Dollar - USD - ${getCurrencySymbol('USD')}`,
  },
  {
    value: 'EUR',
    label: `Euro - EUR - ${getCurrencySymbol('EUR')}`,
  },
  {
    value: 'GBP',
    label: `British Pound - GBP - ${getCurrencySymbol('GBP')}`,
  },
  {
    value: 'CAD',
    label: `Canadian Dollar - CAD - ${getCurrencySymbol('CAD')}`,
  },
  {
    value: 'AUD',
    label: `Australian Dollar - AUD - ${getCurrencySymbol('AUD')}`,
  },
  {
    value: 'JPY',
    label: `Japanese Yen - JPY - ${getCurrencySymbol('JPY')}`,
  },
  {
    value: 'CNY',
    label: `Chinese Yuan - CNY - ${getCurrencySymbol('CNY')}`,
  },
  {
    value: 'CHF',
    label: `Swiss Franc - CHF - ${getCurrencySymbol('CHF')}`,
  },
  {
    value: 'SGD',
    label: `Singapore Dollar - SGD - ${getCurrencySymbol('SGD')}`,
  },
  {
    value: 'HKD',
    label: `Hong Kong Dollar - HKD - ${getCurrencySymbol('HKD')}`,
  },
  {
    value: 'NZD',
    label: `New Zealand Dollar - NZD - ${getCurrencySymbol('NZD')}`,
  },
  {
    value: 'SEK',
    label: `Swedish Krona - SEK - ${getCurrencySymbol('SEK')}`,
  },
  {
    value: 'NOK',
    label: `Norwegian Krone - NOK - ${getCurrencySymbol('NOK')}`,
  },
  {
    value: 'DKK',
    label: `Danish Krone - DKK - ${getCurrencySymbol('DKK')}`,
  },
  {
    value: 'PLN',
    label: `Polish Zloty - PLN - ${getCurrencySymbol('PLN')}`,
  },
  {
    value: 'CZK',
    label: `Czech Koruna - CZK - ${getCurrencySymbol('CZK')}`,
  },
  {
    value: 'HUF',
    label: `Hungarian Forint - HUF - ${getCurrencySymbol('HUF')}`,
  },
  {
    value: 'RON',
    label: `Romanian Leu - RON - ${getCurrencySymbol('RON')}`,
  },
  {
    value: 'UAH',
    label: `Ukrainian Hryvnia - UAH - ${getCurrencySymbol('UAH')}`,
  },
  {
    value: 'MXN',
    label: `Mexican Peso - MXN - ${getCurrencySymbol('MXN')}`,
  },
  {
    value: 'BRL',
    label: `Brazilian Real - BRL - ${getCurrencySymbol('BRL')}`,
  },
]

export const CURRENCY_LABELS = {
  USD: `US Dollar - USD - ${getCurrencySymbol('USD')}`,
  EUR: `Euro - EUR - ${getCurrencySymbol('EUR')}`,
  GBP: `British Pound - GBP - ${getCurrencySymbol('GBP')}`,
  CAD: `Canadian Dollar - CAD - ${getCurrencySymbol('CAD')}`,
  AUD: `Australian Dollar - AUD - ${getCurrencySymbol('AUD')}`,
  JPY: `Japanese Yen - JPY - ${getCurrencySymbol('JPY')}`,
  CNY: `Chinese Yuan - CNY - ${getCurrencySymbol('CNY')}`,
  CHF: `Swiss Franc - CHF - ${getCurrencySymbol('CHF')}`,
  SGD: `Singapore Dollar - SGD - ${getCurrencySymbol('SGD')}`,
  HKD: `Hong Kong Dollar - HKD - ${getCurrencySymbol('HKD')}`,
  NZD: `New Zealand Dollar - NZD - ${getCurrencySymbol('NZD')}`,
  SEK: `Swedish Krona - SEK - ${getCurrencySymbol('SEK')}`,
  NOK: `Norwegian Krone - NOK - ${getCurrencySymbol('NOK')}`,
  DKK: `Danish Krone - DKK - ${getCurrencySymbol('DKK')}`,
  PLN: `Polish Zloty - PLN - ${getCurrencySymbol('PLN')}`,
  CZK: `Czech Koruna - CZK - ${getCurrencySymbol('CZK')}`,
  HUF: `Hungarian Forint - HUF - ${getCurrencySymbol('HUF')}`,
  RON: `Romanian Leu - RON - ${getCurrencySymbol('RON')}`,
  UAH: `Ukrainian Hryvnia - UAH - ${getCurrencySymbol('UAH')}`,
  MXN: `Mexican Peso - MXN - ${getCurrencySymbol('MXN')}`,
  BRL: `Brazilian Real - BRL - ${getCurrencySymbol('BRL')}`,
}
